import z from "zod";

import { InsuredContactShape } from "./insured_contact";

export const UASContactSchema = z.object({
	...InsuredContactShape,
	nameOfInsured: z.string().describe("the name of the insured. Might be a company or an individual"),
	address: z.object({
		city: z.string(),
		state: z.string().describe("State of the insured. Should be 2 letters, like TX for Texas"),
		streetAddress: z.string(),
		zip: z.string(),
	}).describe("Address of the insured"),
	brokerage: z.string().describe("the name of the brokerage submitting the submission"),
	brokerName: z.string().describe("the name of the broker submitting the submission").nullable().default(null),
	brokerEmail: z.string().describe("the email of the broker submitting the submission. Sometimes submissions are forwarded from classa.ai emails. classa.ai emails are never the broker.").nullable().default(null),
});

export const UASTermsSchema = z.object({
	inceptionDate: z.iso.date().describe("the date in which the policy would start. For an existing policy, this would be the existing expiration date. For a new purchase, this would be the closing date of the purchase.").nullable().default(null),
	operationsDescription: z.string().describe("A brief 1-3 sentence description of the insured's unmanned aircraft operations").nullable().default(null),
});

export const UASAircraftSchema = z.object({
	serialNumber: z.string().describe("the manufacturer serial number of the unmanned aircraft (often labelled SN or S/N). Never the FAA registration number"),
	faaRegistration: z.string().nullable().default(null).describe("the FAA drone registration number, usually 10 characters starting with FA (e.g. FA3RPKEHM9). Brokers often list it under \"FAA N#\" or \"Registration\". Set to null if not provided"),
	make: z.string(),
	model: z.string(),
	year: z.string().nullable().describe("the 4 digit year of the unmanned aircraft. Set to null if unknown"),
	insuredValue: z.number().nullable().default(null).describe("the insured value of the unmanned aircraft, including its payload. Set to null if not stated"),
	liabilityLimit: z.number().nullable().default(null).describe("the requested liability limit for the unmanned aircraft. Set to null if not stated"),
	agricultural: z.boolean().nullable().default(null).describe("true if the unmanned aircraft is used for aerial application of seeds, fertilizer, or agricultural chemicals. Leave null if unknown"),
});

export const UASPilotSchema = z.object({
	name: z.string().describe("the full name of the remote pilot"),
	part107CertificateNumber: z.string().nullable().default(null).describe("the pilot's FAA Part 107 remote pilot certificate number. Leave null if not provided"),
	totalHours: z.number().nullable().default(null).describe("the pilot's total flight time in hours across all aircraft, including manned aircraft. Set to null if not stated"),
	uasHours: z.number().nullable().default(null).describe("the pilot's total hours operating unmanned aircraft. Set to null if not stated"),
	agHours: z.number().nullable().default(null).describe("the pilot's hours of agricultural aerial application flown with unmanned aircraft. Set to null if not stated"),
});

export const UASSubmissionSchema = z.object({
	...UASContactSchema.shape,
	...UASTermsSchema.shape,
	uas: z.array(UASAircraftSchema).describe("a list of all unmanned aircraft that should be insured"),
	pilots: z.array(UASPilotSchema).describe("the remote pilots who will operate the unmanned aircraft"),
	lob: z.literal("uas"),
});

export type UASAircraft = z.output<typeof UASAircraftSchema>;
export type UASPilot = z.output<typeof UASPilotSchema>;
export type UASSubmission = z.output<typeof UASSubmissionSchema>;
